import React, { useCallback, useEffect, useMemo } from "react"

import { useFormContext } from "react-hook-form"
import { usaStates } from "typed-usa-states"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { AutoCompleteElement } from "@front-shared/elements/autoComplete.element"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types"

import type { IDynamicCheckerFormInputData } from "../../../../types/data.types"

export const StateInput: React.FunctionComponent<
    IDynamicFormStateInputProps
> = props => {
    const {
        formState: { errors },
        setValue,
        register,
        unregister,
    } = useFormContext<IDynamicCheckerFormInputData>()

    const availableStates = useMemo<ISelectRenderedOption[]>(() => {
        return usaStates
            .filter(state => !props.excludeStates?.includes(state.abbreviation))
            .map((state, index) => {
                return {
                    key: `${state.abbreviation}-${index}`,
                    value: state.abbreviation,
                    displayValue: state.name,
                }
            })
    }, [usaStates, props.excludeStates])

    const handleSearch = useCallback(
        (searchTerm?: string) => {
            const search = searchTerm?.trim().toLowerCase()

            if (!search) {
                return availableStates
            }

            return availableStates.filter(item => {
                return (
                    item.displayValue.toLowerCase().includes(search) ||
                    item.value.toLowerCase().includes(search)
                )
            })
        },
        [availableStates],
    )

    // For selects with no control prop need to set value manually in form
    useEffect(() => {
        props.defaultValue && setValue("state", props.defaultValue)
    }, [props.defaultValue])

    if (!availableStates.length) {
        return null
    }

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            <AutoCompleteElement
                label="Patient State"
                placeholder="Select one"
                name="state"
                dataFetcher={handleSearch}
                reactHookFormRegister={register as any}
                reactHookFormSet={setValue as any}
                reactHookFormErrors={errors}
                reactHookFormUnregister={unregister as any}
                shouldValidate
                disabled={props.disabled}
                defaultValue={
                    availableStates.find(
                        item => item.value === props.defaultValue,
                    )?.displayValue
                }
                validations={{
                    required: FRONT_VALIDATION_CONFIG.required,
                }}
                renderedOptions={availableStates}
                itemRenderer={item => item}
                isLabelStatic
            />
        </div>
    )
}

interface IDynamicFormStateInputProps {
    className?: string
    defaultValue?: string
    excludeStates?: string[]
    disabled?: boolean
}
